import { Navigate, Route, Routes } from "react-router-dom";
import { MasterLayout } from "../../_metronic/layout/MasterLayout";
import {PERMISSIONS, PATH_ROUTES, AUTHORITIES, REDIRECT_PATH} from "../Constant";
import {hasAccessAuthority} from "../modules/utils/FunctionUtils";
import React, { lazy, Suspense } from "react";
import { LayoutSplashScreen } from "../../_metronic/layout/core";

// Lazy load components
const UserList = lazy(() => import("../modules/manager/component/UserList").then(m => ({ default: m.UserList })));
const HomeXPage = lazy(() => import("../pages/HomeXPage/HomeXPage").then(m => ({ default: m.HomeXPage })));
const OrganizationList = lazy(() => import("../modules/manager/component/OrganizationList").then(m => ({ default: m.OrganizationList })));
const RoleList = lazy(() => import("../modules/manager/component/RoleList").then(m => ({ default: m.RoleList })));
const AuthorityList = lazy(() => import("../modules/manager/component/AuthorityList").then(m => ({ default: m.AuthorityList })));
const DepartmentList = lazy(() => import("../modules/manager/component/DepartmentList").then(m => ({ default: m.DepartmentList })));
const FacultyList = lazy(() => import("../modules/manager/component/FacultyList").then(m => ({ default: m.FacultyList })));
const ChamberList = lazy(() => import("../modules/manager/component/ChamberList").then(m => ({ default: m.ChamberList })));
const BedList = lazy(() => import("../modules/manager/component/BedList").then(m => ({ default: m.BedList })));
const Person = lazy(() => import("../modules/person/Person").then(m => ({ default: m.Person })));
const ServiceList = lazy(() => import("../modules/manager/component/ServiceList"));
const MedicalEquipmentList = lazy(() => import("../modules/manager/component/MedicalEquipmentList"));
const SupplierList = lazy(() => import("../modules/manager/component/SupplierList"));
const PackageList = lazy(() => import("../modules/manager/component/PackageList"));
const CategoryList = lazy(() => import("../modules/manager/component/CategoryList").then(m => ({ default: m.CategoryList })));
const TermModelList = lazy(() => import('../modules/manager/component/TermModelList'));
const TermFormBuilder = lazy(() => import("../modules/manager/component/TermFormBuilder"));
const SystemTabsContainer = lazy(() => import("../modules/component/workflows/components/SystemTabsContainers"));
const CacheManagementPage = lazy(() => import("../pages/CacheManagement/CacheManagementPage").then(m => ({ default: m.CacheManagementPage })));
const AppSetting = lazy(() => import('../modules/app-setting').then(m => ({ default: m.AppSetting })));

interface PrivateRouteProps {
  auth: string;
  ability: string;
  component: React.ComponentType<any>;
  redirect: string;
}

const PrivateRoutes = () => {
  const PrivateRoute: React.FC<PrivateRouteProps> = ({auth, ability, component: Component, redirect,}) => {
    return hasAccessAuthority(auth, ability) ? (
      <Suspense fallback={<LayoutSplashScreen />}>
        <Component/>
      </Suspense>
    ) : (
      <Navigate to={redirect}/>
    );
  };

  const linkTo = "/home-x"

  return (
    <Routes>
      <Route index element={<Navigate to={linkTo} />} />
      <Route path="home" element={<Navigate to="/home-x" />} />
      <Route path="home-x" element={
        <Suspense fallback={<LayoutSplashScreen />}>
          <HomeXPage />
        </Suspense>
      } />
      <Route path="/*" element={<Navigate to={linkTo} />} />
      <Route element={<MasterLayout />}>
        {/* Pages */}
        <Route
          path={PATH_ROUTES.SYSTEM.USER + "/*"}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.USER.VIEW}
              component={UserList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.ORGANIZATION + "/*"}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.ORGANIZATION.VIEW}
              component={OrganizationList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.DEPARTMENT}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.DEPARTMENT.VIEW}
              component={DepartmentList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.FACULTY}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.FACULTY.VIEW}
              component={FacultyList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.CHAMBER}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.CHAMBER.VIEW}
              component={ChamberList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.BED}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.BED.VIEW}
              component={BedList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.AUTHORITY}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.AUTHORITY.VIEW}
              component={AuthorityList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.ROLE}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.ROLE.VIEW}
              component={RoleList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.PERSON}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.PERSON.VIEW}
              component={Person}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.SERVICE}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.USER.VIEW}
              component={ServiceList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.PACKAGE}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.USER.VIEW}
              component={PackageList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.EQUIPMENT}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.USER.VIEW}
              component={MedicalEquipmentList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.SUPPLIER}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.USER.VIEW}
              component={SupplierList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.CATEGORY}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.CATEGORY.VIEW}
              component={CategoryList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.CONFIGURATION}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.CONFIGURATION.VIEW}
              component={AppSetting}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.TERM_MODEL}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.TERM_MODEL.VIEW}
              component={TermModelList}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.TERM_FORM_BUILDER}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.TERM_MODEL.VIEW}
              component={TermFormBuilder}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.TERM_FORM_BUILDER_CREATE}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.TERM_MODEL.VIEW}
              component={TermFormBuilder}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.WF_CONFIG}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.TERM_MODEL.VIEW}
              component={SystemTabsContainer}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        <Route
          path={PATH_ROUTES.SYSTEM.CACHE}
          element={
            <PrivateRoute
              auth={PERMISSIONS.ADMIN}
              ability={AUTHORITIES.CACHE.VIEW}
              component={CacheManagementPage}
              redirect={REDIRECT_PATH.HOME}
            />
          }
        />
        {/* Page Not Found */}
        <Route path="*" element={<Navigate to={REDIRECT_PATH.NOT_FOUND}/>} />
      </Route>
    </Routes>
  );
};

export { PrivateRoutes };

